import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import './global.css';
import { getTheme } from './theme';
import { ThemeModeProvider } from './contexts/ThemeContext';
import { messaging, onMessage } from './firebase';

// Register service worker for Firebase Messaging (web only; skipped/fails harmlessly on iOS)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register(`${process.env.PUBLIC_URL}/firebase-messaging-sw.js`)
    .then((registration) => {
      console.log('Service Worker registered with scope:', registration.scope);
    })
    .catch((error) => {
      console.error('Service Worker registration failed:', error);
    });
}

if (messaging) {
  onMessage(messaging, (payload) => {
    console.log('Message received. ', payload);
  });
}

/**
 * When a MUI Dialog opens, it applies aria-hidden to #root. If focus is still on the
 * trigger button inside #root, we get an a11y violation. Blur the trigger so focus
 * leaves #root (goes to body), then MUI can move focus into the dialog.
 */
function useDialogAriaHiddenFix() {
  React.useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    const isModalNode = (node) => {
      if (!node || node.nodeType !== 1) return false;
      const cls = node.classList;
      return Boolean(
        cls?.contains("MuiModal-root") ||
          cls?.contains("MuiDialog-root") ||
          node.getAttribute("role") === "presentation" && cls?.contains("MuiPopover-root")
      );
    };
    const observer = new MutationObserver((mutations) => {
      const addedModal = mutations.some((m) =>
        [...m.addedNodes].some(isModalNode)
      );
      if (!addedModal) return;
      const active = document.activeElement;
      if (!active || !root.contains(active)) return;
      const tag = active.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || active.isContentEditable) {
        return;
      }
      active.blur();
    });
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);
}

function ThemedApp() {
  // Light mode is incomplete across the app — lock to dark until a full pass exists.
  const theme = getTheme('dark');
  useDialogAriaHiddenFix();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <ThemeModeProvider>
    <ThemedApp />
  </ThemeModeProvider>
);
